import React, { FormEvent, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import toast from 'react-hot-toast';
import { withAuthenticationRequired } from '../providers/withAuthenticationRequired';
import { useAuth } from '../hooks/useAuth';
import { useAssignableRolesQuery, useUsersQuery } from '../queries/users.query';
import {
  useCreateUserMutation,
  useResetUserPasswordMutation,
  useUpdateUserMutation
} from '../mutations/users.mutation';
import { UserSummary } from '../types/auth.types';

type DialogState =
  | { mode: 'create' }
  | { mode: 'edit'; user: UserSummary }
  | { mode: 'reset'; user: UserSummary }
  | null;

// The API stores UTC without an offset, so the JSON may lack the trailing "Z".
const formatUtc = (value?: string | null) =>
  value ? new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`).toLocaleString() : '-';

const errorLines = (e: unknown): string[] => {
  const data = (e as { response?: { data?: { message?: string; details?: string[] } } })?.response
    ?.data;
  return [data?.message ?? 'Request failed.', ...(data?.details ?? [])];
};

function UserDialog({ state, onClose }: { state: DialogState; onClose: () => void }) {
  const { data: roles } = useAssignableRolesQuery();
  const createMutation = useCreateUserMutation();
  const updateMutation = useUpdateUserMutation();
  const resetMutation = useResetUserPasswordMutation();

  const existing = state && state.mode !== 'create' ? state.user : undefined;
  const [email, setEmail] = useState(existing?.email ?? '');
  const [displayName, setDisplayName] = useState(existing?.displayName ?? '');
  const [role, setRole] = useState(existing?.roles?.[0] ?? 'Staff');
  const [isActive, setIsActive] = useState(existing?.isActive ?? true);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  if (!state) return null;
  const busy = createMutation.isLoading || updateMutation.isLoading || resetMutation.isLoading;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setErrors([]);
    try {
      if (state.mode === 'create') {
        await createMutation.mutateAsync({ email, displayName, roles: [role], temporaryPassword });
        toast.success(`User ${email} created. Share the temporary password with them securely.`);
      } else if (state.mode === 'edit') {
        await updateMutation.mutateAsync({
          id: state.user.id,
          request: { displayName, roles: [role], isActive }
        });
        toast.success('User updated.');
      } else {
        await resetMutation.mutateAsync({ id: state.user.id, temporaryPassword });
        toast.success('Temporary password set. The user must change it at next sign-in.');
      }
      onClose();
    } catch (e) {
      setErrors(errorLines(e));
    }
  };

  const title =
    state.mode === 'create'
      ? 'Add user'
      : state.mode === 'edit'
      ? `Edit ${state.user.email}`
      : `Reset password for ${state.user.email}`;

  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {errors.length > 0 && (
            <Alert variant="danger">
              {errors.map((err) => (
                <div key={err}>{err}</div>
              ))}
            </Alert>
          )}
          {state.mode === 'create' && (
            <Form.Group className="mb-3" controlId="userEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>
          )}
          {state.mode !== 'reset' && (
            <>
              <Form.Group className="mb-3" controlId="userDisplayName">
                <Form.Label>Display name</Form.Label>
                <Form.Control
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="userRole">
                <Form.Label>Role</Form.Label>
                <Form.Select value={role} onChange={(e) => setRole(e.target.value)}>
                  {Object.entries(roles ?? { Staff: ['base'], Admin: ['admin', 'base'] }).map(
                    ([name, scopes]) => (
                      <option key={name} value={name}>
                        {name} ({scopes.join(', ')})
                      </option>
                    )
                  )}
                </Form.Select>
              </Form.Group>
            </>
          )}
          {state.mode === 'edit' && (
            <Form.Check
              type="switch"
              id="userActive"
              label="Active (inactive users cannot sign in)"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          )}
          {state.mode !== 'edit' && (
            <Form.Group className="mb-3" controlId="userTempPassword">
              <Form.Label>Temporary password</Form.Label>
              <Form.Control
                type="text"
                autoComplete="off"
                value={temporaryPassword}
                onChange={(e) => setTemporaryPassword(e.target.value)}
                required
              />
              <Form.Text muted>
                At least 10 characters with upper- and lowercase letters and a digit. The user must
                change it at first sign-in.
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function UsersPage() {
  const { hasScope } = useAuth();
  const { data: users, isLoading } = useUsersQuery();
  const [dialog, setDialog] = useState<DialogState>(null);

  if (!hasScope('admin')) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">Only admins can manage users.</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Users</h3>
        <Button onClick={() => setDialog({ mode: 'create' })}>Add user</Button>
      </div>
      {isLoading ? (
        <Spinner animation="border" />
      ) : (
        <Table striped hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last sign-in</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(users ?? []).map((u) => (
              <tr key={u.id}>
                <td>{u.displayName}</td>
                <td>{u.email}</td>
                <td>{u.roles.join(', ')}</td>
                <td>
                  {!u.isActive && <Badge bg="secondary">Inactive</Badge>}{' '}
                  {u.isLockedOut && <Badge bg="danger">Locked</Badge>}{' '}
                  {u.mustChangePassword && <Badge bg="warning">Temporary password</Badge>}
                </td>
                <td>{formatUtc(u.lastLoginAtUtc)}</td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => setDialog({ mode: 'edit', user: u })}>
                    Edit
                  </Button>{' '}
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setDialog({ mode: 'reset', user: u })}>
                    Reset password
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {dialog && (
        <UserDialog
          key={dialog.mode === 'create' ? 'create' : `${dialog.mode}-${dialog.user.id}`}
          state={dialog}
          onClose={() => setDialog(null)}
        />
      )}
    </Container>
  );
}

export default withAuthenticationRequired(UsersPage);
